import { FC, useEffect, useMemo } from "react";
import { Box, Container } from "@mui/material";
import ImgFlagshipSystem from "../../assets/images/home/flagship-system.svg";
import ImgIndexServices from "../../assets/images/home/index-services.svg";
import ImgInnovativeIndex from "../../assets/images/home/innovative-index.svg";
import ImgPortfolioApi from "../../assets/images/home/portfolio-api.svg";
import ImgThematicData from "../../assets/images/home/thematic-data.svg";
import HomeCardCarousel from "../../components/HomeCardCarousel";
import ClientsList from "../../components/HomeClientsList";
import HomeSection from "../../components/HomeSection";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { fetchNewsList, selectNewsList } from "../../redux/slices/news";
import { checkFeatureFlagEnabled } from "../../utils";
import { NEWS_ITEMS } from "./data";
import {
  root,
  Hero,
  HeroMain,
  VideoWrapper,
  VideoBackgroundTop,
  VideoBackground,
  HeroSub,
  Sections,
  NewsTitle,
  TrustedBy,
  heroSubTitle,
  heroSubDescription,
  heroDescription,
  heroTitle,
} from "./styles";

const HomePage: FC<{}> = () => {
  const dispatch = useAppDispatch();
  const newsList = useAppSelector(selectNewsList);

  const NEWS_API_ENABLED = checkFeatureFlagEnabled("NEWS_API_ENABLED");

  const newsItems = useMemo(
    () => (NEWS_API_ENABLED ? newsList : NEWS_ITEMS),
    [NEWS_API_ENABLED, newsList]
  );

  useEffect(() => {
    if (NEWS_API_ENABLED) {
      dispatch(fetchNewsList());
    }
  }, [NEWS_API_ENABLED, dispatch]);

  return (
    <Box sx={{ ...root, backgroundColor: "#0D0F17", color: "#fff" }}>
      <Container>
        <Hero>
          <VideoWrapper>
            <video controls={false} autoPlay muted loop playsInline>
              <source src="/videos/header.mp4" type="video/mp4" />
            </video>
          </VideoWrapper>
          <VideoBackgroundTop />
          <VideoBackground />
          <HeroMain>
            <Box>
              <Box sx={{ ...heroTitle }}>
                The Index Technology
                <br />
                Company
              </Box>
              <Box sx={{ ...heroDescription }}>
                Power the next generation of passive and systematic investing
              </Box>
            </Box>
          </HeroMain>
        </Hero>

        <HeroSub>
          <Box>
            <Box sx={{ ...heroSubTitle }}>The Future of Indexing</Box>
            <Box sx={{ ...heroSubDescription }}>
              BITA is building the world's most technologically advanced index
              platform. Our cloud-based infrastructure leverages the latest
              technologies to deliver passive strategies faster and more
              flexibly than any other indexation solution.
            </Box>
          </Box>
        </HeroSub>

        <Box py={{ xs: 2, md: 20 }}>
          <TrustedBy>Trusted by</TrustedBy>
          <ClientsList />
        </Box>

        <Sections>
          <HomeSection
            subTitle="Indexing Without Boundaries"
            title="Innovative Indexes"
            text={[
              "Thanks to our flexible infrastructure and proprietary IP, we can develop and launch innovative indexes at a fraction of time.",
              "Whether the idea comes from our product development team, or is introduced to us, we make sure our clients are first to market.",
            ]}
            link="/index"
            image={ImgInnovativeIndex}
          />

          <HomeSection
            subTitle="Direct and Self-Indexing at Scale"
            title="BITACore: BITA’s flagship system"
            text={[
              "The most powerful end-to-end index and benchmarks development software. Our BITACore platform can be used by clients to customize any index or systematic strategy in minutes.",
              "Access a large variety of datasets, bring your own data, customize, backtest, and quickly generate regulatory compliant reports and analytics. All within one platform.",
            ]}
            link="/products"
            image={ImgFlagshipSystem}
            direction="reverse"
          />

          <HomeSection
            subTitle="The Home of Pure Thematics"
            title="Granular Thematic Data and Research"
            text={[
              "The world’s most complete and granular thematic database. Going all the way to products, services and activities, our thematic database allows clients to uncover and quantify the true thematic exposure of a company or portfolio.",
              "Available via data licenses, through indexes, APIs, or via our BITACore and BITALens platforms.",
            ]}
            link="/products/thematic-data"
            image={ImgThematicData}
          />

          <HomeSection
            subTitle="Complete Customization"
            title="Indexing API’s"
            text={[
              "Bring your product development, analytics, and reporting to the next level. From investment customization, backtesting, ESG and thematic analytics, to regulatory reporting, our suite of indexing APIs have you covered.",
            ]}
            link="/indexing-apis"
            image={ImgPortfolioApi}
            direction="reverse"
          />

          <HomeSection
            subTitle="Indexing as Service"
            title="Indexing and Calculation Services"
            text={[
              "BITA offers highly flexible and customizable index calculation, iNAV/iOPV calculation, index research, and index administration services.",
              "Our clients can run successful indexing businesses while we take care of infrastructure, operations and regulation.",
            ]}
            link="/index-services"
            image={ImgIndexServices}
          />
        </Sections>

        {newsList?.length ? (
          <Box pt={{ xs: 0, md: 4 }} pb={12.5}>
            <NewsTitle>Latest News</NewsTitle>
            <HomeCardCarousel data={newsItems} />
          </Box>
        ) : null}
      </Container>
    </Box>
  );
};

export default HomePage;
